'use client';

import React, { type ReactNode } from 'react';
import type { Brand } from '@/domainLogic/index.ts';
import Navigation from '@/app/components/navigation.tsx';
import SearchPageDataProvider from '@/app/components/search/searchPageDataProvider.tsx';
import { CSWAppStickyWrapper, HeaderContainer, TsxMainContent, TsxNavigation } from '@/app/common/styled.ts';
import LoadLine from '@/app/components/Loadline.tsx';
import { StickyNavProvider } from '@/app/common/components/StickyNavBar/StickyNavContext.tsx';
import StickyNavHeader from '@/app/common/components/StickyNavBar/StickyNavHeader.tsx';
import { TopMargin } from '@/app/components/topMargin';
import type { SearchPageData } from '@/app/search/createSearchPageData.ts';
import type { PAGE_TYPE } from '@/app/common/pageType.ts';
import { UBUNTU_FONT_STACK } from '@/utils/ubuntuFont';
import HeaderManager, { type HeaderManagerProps } from './HeaderManager';
import { resolveHeaderVisibility } from './headerVisibility';

type NavigationManagerProps = {
  evolutionResultsOnlyEnabled: boolean;
  isApp: boolean;
  appShowCsw: boolean;
  externalHeaderEnabled: boolean;
  /** Header scrolls away with the page instead of being fixed/sticky (evo detail steps). */
  staticHeader?: boolean;
  brand: Brand;
  showGlobalLoadLine: boolean;
  showNavigation: boolean;
  pageType: PAGE_TYPE;
  handleGoToNextStep: (pageType: PAGE_TYPE) => void;
  searchPageData: SearchPageData;
  /** showCsw is resolved here from pageType, not passed down — see resolveHeaderVisibility. */
  headerProps: Omit<HeaderManagerProps, 'showCsw'>;
  children: ReactNode;
};

const NavigationManager = ({
  evolutionResultsOnlyEnabled,
  isApp,
  appShowCsw,
  externalHeaderEnabled,
  staticHeader,
  brand,
  showGlobalLoadLine,
  showNavigation,
  pageType,
  handleGoToNextStep,
  searchPageData,
  headerProps,
  children,
}: NavigationManagerProps) => {
  const { showCsw, showExternalCSWDetail, showHeader, showAppCswOnly } = resolveHeaderVisibility({
    pageType,
    isApp,
    externalHeaderEnabled,
    appShowCsw,
  });
  // The relative wrapper anchors the global load line to the CSW's bottom edge.
  const appCswHeader = (
    <div style={{ position: 'relative' }}>
      <HeaderManager {...headerProps} showCsw={showCsw} />
      {showGlobalLoadLine && <LoadLine $isGlobal={true} />}
    </div>
  );
  if (evolutionResultsOnlyEnabled) {
    const header = (
      <HeaderContainer
        externalHeaderEnabled={externalHeaderEnabled}
        showExternalCSW={showCsw}
        brand={brand}
        evolutionEnabled={true}
        staticHeader={staticHeader}
      >
        <HeaderManager {...headerProps} showCsw={showCsw} />
        {showGlobalLoadLine && <LoadLine $isGlobal={true} style={{ position: 'absolute', marginTop: 64, top: 0 }} />}
      </HeaderContainer>
    );
    return (
      <StickyNavProvider>
        {showHeader &&
          (showAppCswOnly ? (
            // Web parity: slide-out/in on scroll; evo detail steps scroll away.
            staticHeader ? (
              appCswHeader
            ) : (
              <StickyNavHeader>{appCswHeader}</StickyNavHeader>
            )
          ) : staticHeader ? (
            header
          ) : (
            <StickyNavHeader>{header}</StickyNavHeader>
          ))}
        {/* No TopMargin with a static header or the app CSW: both occupy real space in the flow. */}
        {showHeader && !staticHeader && !showAppCswOnly && (
          <TopMargin externalHeaderEnabled={externalHeaderEnabled} brand={brand} evolutionEnabled={true} />
        )}
        {showNavigation && <Navigation currentPage={pageType} handleGoToNextStep={handleGoToNextStep} />}
        <TsxMainContent
          role="region"
          style={{
            background: 'white',
            fontFamily: UBUNTU_FONT_STACK,
          }}
        >
          <SearchPageDataProvider searchPageData={searchPageData}>{children}</SearchPageDataProvider>
        </TsxMainContent>
      </StickyNavProvider>
    );
  }

  return (
    <>
      {/* Web parity: always pinned, like the fixed non-evo header. Must stay
          outside TsxNavigation (see CSWAppStickyWrapper). */}
      {showHeader && showAppCswOnly && <CSWAppStickyWrapper>{appCswHeader}</CSWAppStickyWrapper>}
      <TsxNavigation>
        {showHeader && !showAppCswOnly && (
          <HeaderContainer
            externalHeaderEnabled={externalHeaderEnabled}
            showExternalCSW={showCsw}
            brand={brand}
            evolutionEnabled={false}
          >
            <HeaderManager {...headerProps} showCsw={showCsw} />
            {showGlobalLoadLine && <LoadLine $isGlobal={true} style={{ marginTop: 64, top: 0 }} />}
          </HeaderContainer>
        )}
        {showHeader && !showAppCswOnly && (
          <TopMargin
            externalHeaderEnabled={externalHeaderEnabled}
            brand={brand}
            evolutionEnabled={false}
            showExternalCSWDetail={showExternalCSWDetail}
          />
        )}
        {showNavigation && <Navigation currentPage={pageType} handleGoToNextStep={handleGoToNextStep} />}
      </TsxNavigation>
      <TsxMainContent role="region" style={{ fontFamily: UBUNTU_FONT_STACK }}>
        <SearchPageDataProvider searchPageData={searchPageData}>{children}</SearchPageDataProvider>
      </TsxMainContent>
    </>
  );
};

export default NavigationManager;
